import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Scrolls to the element whose id matches the current URL hash.
 * Retries briefly because sections may still be mounting when the hash arrives.
 * Use inside any single-page component that has anchor-able sections.
 */
export default function useScrollToHash(offset = 80) {
  const { hash, pathname } = useLocation();

  useEffect(() => {
    if (!hash) {
      // New route, no hash → reset to top.
      window.scrollTo({ top: 0, behavior: "auto" });
      return;
    }

    const id = hash.replace("#", "");
    let attempts = 0;
    const maxAttempts = 20;

    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top, behavior: "smooth" });
        return true;
      }
      return false;
    };

    if (tryScroll()) return;

    const interval = setInterval(() => {
      attempts += 1;
      if (tryScroll() || attempts >= maxAttempts) {
        clearInterval(interval);
      }
    }, 50);

    return () => clearInterval(interval);
  }, [hash, pathname, offset]);
}